import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { getBodyEnd, getViewLog, logView, getWeeklyArticleHistory, } from '@guardian/automat-client';
import { isRecurringContributor, getLastOneOffContributionTimestamp, shouldHideSupportMessaging, hasCmpConsentForArticleCount, getEmail, MODULES_VERSION, hasOptedOutOfArticleCount, } from '@root/src/web/lib/contributions';
import { getForcedVariant } from '@root/src/web/lib/readerRevenueDevUtils';
import { initPerf } from '@root/src/web/browser/initPerf';
import { sendOphanComponentEvent, submitComponentEvent, } from '@root/src/web/browser/ophan/ophan';
import { setAutomat } from '@root/src/web/lib/setAutomat';
import { cmp } from '@guardian/consent-management-platform';
import { getCookie } from '../../browser/cookie';
import { useHasBeenSeen } from '../../lib/useHasBeenSeen';
const checkForErrors = (response) => {
    if (!response.ok) {
        throw Error(response.statusText ||
            `ReaderRevenueEpic | An api call returned HTTP status ${response.status}`);
    }
    return response;
};
const wrapperMargins = css `
	margin: 18px 0;
`;
const buildPayload = async (data) => {
    return {
        tracking: {
            ophanPageId: window.guardian.config.ophan.pageViewId,
            ophanComponentId: '',
            platformId: 'GUARDIAN_WEB',
            clientName: 'dcr',
            referrerUrl: window.location.origin + window.location.pathname,
        },
        targeting: {
            contentType: data.contentType,
            sectionName: data.sectionName || '',
            shouldHideReaderRevenue: data.shouldHideReaderRevenue,
            isMinuteArticle: data.isMinuteArticle,
            isPaidContent: data.isPaidContent,
            tags: data.tags,
            showSupportMessaging: !shouldHideSupportMessaging(data.isSignedIn || false),
            isRecurringContributor: isRecurringContributor(data.isSignedIn || false),
            lastOneOffContributionDate: getLastOneOffContributionTimestamp(),
            epicViewLog: getViewLog(),
            weeklyArticleHistory: getWeeklyArticleHistory(),
            hasOptedOutOfArticleCount: await hasOptedOutOfArticleCount(),
            mvtId: Number(getCookie('GU_mvt_id')),
            countryCode: data.countryCode,
            modulesVersion: MODULES_VERSION,
            url: window.location.origin + window.location.pathname,
        },
    }; // Metadata type incorrectly does not include required hasOptedOutOfArticleCount property
};
export const canShow = async (data) => {
    const { isSignedIn, shouldHideReaderRevenue, isPaidContent, contributionsServiceUrl, idApiUrl, stage, } = data;
    if (shouldHideReaderRevenue || isPaidContent) {
        // We never serve Reader Revenue epics in this case
        return Promise.resolve({ show: false });
    }
    const dataPerf = initPerf('contributions-epic-data');
    dataPerf.start();
    const forcedVariant = getForcedVariant('epic');
    const queryString = forcedVariant ? `?force=${forcedVariant}` : '';
    const contributionsPayload = await buildPayload(data);
    const response = await getBodyEnd(contributionsPayload, `${contributionsServiceUrl}/epic${queryString}`);
    checkForErrors(response);
    const json = await response.json();
    if (!json.data) {
        return { show: false };
    }
    const email = isSignedIn ? await getEmail(idApiUrl) : undefined;
    const hasConsentForArticleCount = await hasCmpConsentForArticleCount();
    const { meta, module } = json.data;
    return {
        show: true,
        meta: {
            meta,
            module,
            email,
            hasConsentForArticleCount,
            stage,
        },
    };
};
export const ReaderRevenueEpic = ({ meta, module, email, hasConsentForArticleCount, stage, }) => {
    const [Epic, setEpic] = useState();
    const [hasBeenSeen, setNode] = useHasBeenSeen({
        rootMargin: '-18px',
        threshold: 0,
        debounce: true,
    });
    const openCmp = () => {
        cmp.showPrivacyManager();
    };
    useEffect(() => {
        setAutomat();
        const modulePerf = initPerf('contributions-epic-module');
        modulePerf.start();
        window
            .guardianPolyfilledImport(module.url)
            .then((epicModule) => {
            modulePerf.end();
            setEpic(() => epicModule.ContributionsEpic); // useState requires functions to be wrapped
            sendOphanComponentEvent('INSERT', meta);
        })
            .catch((error) => {
            const msg = `Error importing RR epic: ${error}`;
            // eslint-disable-next-line no-console
            console.log(msg);
            window.guardian.modules.sentry.reportError(new Error(msg), 'rr-epic');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (hasBeenSeen && meta) {
            // Should only run once
            const { abTestName } = meta;
            logView(abTestName);
            sendOphanComponentEvent('VIEW', meta);
        }
    }, [hasBeenSeen, meta]);
    if (Epic) {
        return (_jsx("div", Object.assign({ ref: setNode, css: wrapperMargins }, { children: _jsx(Epic, Object.assign({}, module.props, { email: email, submitComponentEvent: submitComponentEvent, openCmp: openCmp, hasConsentForArticleCount: hasConsentForArticleCount, stage: stage }), void 0) }), void 0));
    }
    return null;
};
